import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { formatDate } from '../../shared/utils/format';
import { Badge, Card, CardBody, CardHeader, LoadingSkeleton } from '@nexora/design-system';

/* ── Contrato real ──
 * GET /logistics/deliveries/monitor → { data: MonitorItemDto[] }
 *   MonitorItemDto { cte: { number, serie, issuedAt, status, recipientName? },
 *                    ultimaOcorrencia: { tipo, ocorridoEm, descricao? } | null }
 */

interface MonitorItem {
  cte: {
    number: string;
    serie?: string | null;
    issuedAt?: string | null;
    status: string;
    recipientName?: string | null;
    recipientId?: string;
  };
  ultimaOcorrencia: {
    tipo: string;
    ocorridoEm: string;
    descricao?: string | null;
  } | null;
}

const STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'processing' | 'critical'> = {
  DRAFT: 'neutral',
  EMITIDO: 'info',
  TRANSPORTE: 'processing',
  ENTREGUE: 'success',
  CANCELADO: 'error',
};

const OCORRENCIA_LABEL: Record<string, string> = {
  COLETA: 'Coleta',
  TRANSITO: 'Em trânsito',
  ROTA: 'Em rota',
  TENTATIVA_ENTREGA: 'Tentativa de entrega',
  ENTREGUE: 'Entregue',
  AVARIA: 'Avaria',
  DEVOLUCAO: 'Devolução',
};

export function DeliveriesMonitorPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const monitorQ = useQuery({
    queryKey: ['logistics', 'deliveries', 'monitor'],
    queryFn: async () => {
      const data = await apiClient.get<any>('/logistics/deliveries/monitor');
      return data as { data: MonitorItem[] };
    },
    enabled: !!tenantId,
  });

  const items = monitorQ.data?.data ?? [];

  return (
    <Card variant="outlined" padding="0" className="page-card">
      <CardHeader className="px-md pt-md">Monitoramento CT-e — Conhecimentos recentes e última ocorrência</CardHeader>
      <CardBody>
        {monitorQ.isLoading ? (
          <LoadingSkeleton />
        ) : items.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--on-surface-variant)' }}>
            Nenhum conhecimento em monitoramento.
          </div>
        ) : (
          <table className="ds-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>CT-e / Série</th>
                <th>Emissão</th>
                <th>Destinatário</th>
                <th>Status Atual</th>
                <th>Última Atualização</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.cte.number}>
                  <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {m.cte.number}{m.cte.serie ? ` / ${m.cte.serie}` : ''}
                  </td>
                  <td>{m.cte.issuedAt ? formatDate(m.cte.issuedAt) : '—'}</td>
                  <td>{m.cte.recipientName ?? m.cte.recipientId?.slice(0, 8) ?? '—'}</td>
                  <td>
                    <Badge variant={STATUS_VARIANT[m.cte.status] ?? 'neutral'} size="sm">{m.cte.status}</Badge>
                  </td>
                  <td>
                    {m.ultimaOcorrencia ? (
                      <>
                        <Badge variant="info" size="sm">{OCORRENCIA_LABEL[m.ultimaOcorrencia.tipo] ?? m.ultimaOcorrencia.tipo}</Badge>{' '}
                        <span style={{ fontSize: '0.8rem', color: 'var(--on-surface-variant)' }}>
                          {formatDate(m.ultimaOcorrencia.ocorridoEm)}
                        </span>
                        {m.ultimaOcorrencia.descricao ? (
                          <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-variant)' }}>{m.ultimaOcorrencia.descricao}</div>
                        ) : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}